import { AxiosRequestConfig } from 'axios';
import { HttpResponse, ajax } from '../../../../common/ajax';
import { config } from '../../config';
import {
  AddCodesCommand,
  AddProductCommand,
  DeactivateProductCommand,
  EditProductCommand,
  EditProductReadModel,
  ProductItemModel,
  ProductStatusModel,
  ProductTypeModel,
} from '../../models/products';

interface IProductsService {
  getProducts: (
    queryString?: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ProductItemModel[]>>;
  getProductStatuses: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ProductStatusModel[]>>;
  getProductTypes: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ProductTypeModel[]>>;
  getEditProductReadModel: (
    productId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<EditProductReadModel>>;
  addProduct: (
    data: AddProductCommand,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<number>>;
  editProduct: (
    data: EditProductCommand,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  addCodes: (
    data: AddCodesCommand,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  addProductImage: (
    data: FormData,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<string>>;
  removeProductImage: (
    image: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  deactivateProduct: (
    data: DeactivateProductCommand,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  duplicateProduct: (
    productId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<number>>;
  cancelProductApproval: (
    productId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  cancelProductDeactivation: (
    productId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  validateProductApproval: (
    productId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  sendProductForApproval: (
    productId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  deleteDraft: (
    productId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  downloadProductsExcel: (
    filename: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<null>>;
}

export const productsService: IProductsService = {
  getProducts(queryString, axiosConfig) {
    return ajax.get<ProductItemModel[]>(
      `${config.merchantsApiUrl}/odata/products?${queryString}`,
      axiosConfig,
    );
  },
  getProductStatuses(axiosConfig) {
    return ajax.get<ProductStatusModel[]>(
      `${config.merchantsApiUrl}/products/statuses`,
      axiosConfig,
    );
  },
  getProductTypes(axiosConfig) {
    return ajax.get<ProductTypeModel[]>(
      `${config.merchantsApiUrl}/products/types`,
      axiosConfig,
    );
  },
  getEditProductReadModel(productId, axiosConfig) {
    return ajax.get<EditProductReadModel>(
      `${config.merchantsApiUrl}/products/${productId}/edit`,
      axiosConfig,
    );
  },
  addProduct(data, axiosConfig) {
    return ajax.post<number>(
      `${config.merchantsApiUrl}/products`,
      data,
      axiosConfig,
    );
  },
  editProduct(data, axiosConfig) {
    return ajax.put<boolean>(
      `${config.merchantsApiUrl}/products/${data.productId}/edit`,
      data,
      axiosConfig,
    );
  },
  addCodes(data, axiosConfig) {
    return ajax.put<boolean>(
      `${config.merchantsApiUrl}/products/${data.productId}/add-codes`,
      data,
      axiosConfig,
    );
  },
  addProductImage(data, axiosConfig) {
    return ajax.post<string>(
      `${config.merchantsApiUrl}/products/image`,
      data,
      axiosConfig,
    );
  },
  removeProductImage(image, axiosConfig) {
    return ajax.delete<boolean>(
      `${config.merchantsApiUrl}/products/image/${image}`,
      axiosConfig,
    );
  },
  deactivateProduct(data, axiosConfig) {
    return ajax.post<boolean>(
      `${config.merchantsApiUrl}/products/${data.productId}/deactivate`,
      data,
      axiosConfig,
    );
  },
  duplicateProduct(productId, axiosConfig) {
    return ajax.post<number>(
      `${config.merchantsApiUrl}/products/${productId}/duplicate`,
      axiosConfig,
    );
  },
  cancelProductApproval(productId, axiosConfig) {
    return ajax.post<boolean>(
      `${config.merchantsApiUrl}/products/${productId}/cancel-approval`,
      axiosConfig,
    );
  },
  cancelProductDeactivation(productId, axiosConfig) {
    return ajax.post<boolean>(
      `${config.merchantsApiUrl}/products/${productId}/cancel-deactivation`,
      axiosConfig,
    );
  },
  validateProductApproval(productId, axiosConfig) {
    return ajax.get<boolean>(
      `${config.merchantsApiUrl}/products/${productId}/validate-approval`,
      axiosConfig,
    );
  },
  sendProductForApproval(productId, axiosConfig) {
    return ajax.post<boolean>(
      `${config.merchantsApiUrl}/products/${productId}/send-approval`,
      axiosConfig,
    );
  },
  deleteDraft(productId, axiosConfig) {
    return ajax.delete<boolean>(
      `${config.merchantsApiUrl}/products/${productId}`,
      axiosConfig,
    );
  },
  downloadProductsExcel(filename, axiosConfig) {
    return ajax.downloadFile(
      `${config.merchantsApiUrl}/products/download`,
      filename,
      'xlsx',
      axiosConfig,
    );
  },
};
